/**
 * SLA Badge Component
 *
 * Displays a coloured pill/badge on Kanban cards showing:
 *  - Days the ticket has been open
 *  - Days until (or past) the resolution SLA deadline
 *  - Current SLA stage (Triage / Allocation / Resolution)
 *
 * Colour logic:
 *   🟢 Green  — all stages ok, > 50% of SLA time remaining
 *   🟡 Amber  — warning (< 50% remaining on any active stage)
 *   🔴 Red    — breached (any active stage past deadline)
 *   ⚫ Grey   — no SLA set
 */

import React from 'react';
import { TicketListItem } from '@/types';

interface SLABadgeProps {
  ticket: TicketListItem;
  compact?: boolean;
}

type SLAColour = 'green' | 'amber' | 'red' | 'grey';

const COLOUR_CLASSES: Record<SLAColour, string> = {
  green: 'bg-green-100 text-green-800 border-green-300',
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  red:   'bg-red-100   text-red-800   border-red-300',
  grey:  'bg-gray-100  text-gray-500  border-gray-300',
};

const DOT_CLASSES: Record<SLAColour, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red:   'bg-red-500',
  grey:  'bg-gray-400',
};

function getColour(status?: string): SLAColour {
  if (!status || status === 'ok' || status === 'met') return 'green';
  if (status === 'warning') return 'amber';
  if (status === 'breached') return 'red';
  return 'grey';
}

function formatDays(days?: number | null): string {
  if (days == null) return '—';
  if (days > 0) return `+${days.toFixed(0)}d`;
  if (days < 0) return `${Math.abs(Math.ceil(days))}d left`;
  return 'today';
}

function getStageLabel(slaStages?: TicketListItem['sla_stage_statuses']): string {
  if (!slaStages) return 'RES';
  if (slaStages.triage === 'ok' || slaStages.triage === 'met') {
    // Triage done, check allocation
    if (slaStages.allocation === 'ok' || slaStages.allocation === 'met') {
      return 'RES';
    }
    return 'ALLOC';
  }
  return 'TRIAGE';
}

export function SLABadge({ ticket, compact = false }: SLABadgeProps) {
  const hasSLA = !!(
    ticket.sla_resolution_deadline ||
    ticket.sla_triage_deadline ||
    ticket.sla_due_at
  );

  if (!hasSLA) return null;

  const stages = ticket.sla_stage_statuses;
  const overallStatus = stages?.overall ?? ticket.sla_status ?? 'ok';
  const colour = getColour(overallStatus);
  const daysOpen = stages?.days_open ?? ticket.days_open ?? 0;
  const daysUntil = stages?.days_until_resolution ?? ticket.days_until_resolution;
  const stageLabel = getStageLabel(stages);

  if (compact) {
    // Compact mode: just a coloured dot
    return (
      <span
        className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT_CLASSES[colour]}`}
        title={`SLA ${overallStatus} · ${daysOpen}d open`}
      />
    );
  }

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium
        ${COLOUR_CLASSES[colour]}
      `}
      title={`Stage: ${stageLabel} · ${daysOpen} day(s) open · SLA ${overallStatus}`}
    >
      {/* Pulsing dot for breached */}
      <span
        className={`
          w-2 h-2 rounded-full flex-shrink-0
          ${DOT_CLASSES[colour]}
          ${colour === 'red' ? 'animate-pulse' : ''}
        `}
      />

      {/* Days open */}
      <span>{daysOpen}d</span>

      {/* Divider */}
      <span className="opacity-40">·</span>

      {/* Stage + deadline */}
      <span className="opacity-75 text-[10px] uppercase tracking-wide">{stageLabel}</span>
      {daysUntil != null && (
        <>
          <span className="opacity-40">·</span>
          <span>{formatDays(-daysUntil)}</span>
        </>
      )}
    </div>
  );
}
